/**
 * Listado único de equipamiento y merch. Conteos y página salen de la RPC.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { dbClient, normalizeRpcResponse } from '@/services/db-client';
import { useAuth } from '@/hooks/useAuth';
import { refreshStoreAfter } from '../storeRefresh';
import { PRODUCT_PAGE_SIZE } from './productListModel';

export interface StoreProductRow {
    id: string;
    kind: 'catalog' | 'merch';
    sourceId: string;
    name: string;
    slug: string;
    brand: string;
    originKey: string;
    originLabel: string;
    qualityStatus: string | null;
    isActive: boolean;
    completeness: number | null;
    stockCode: string;
    stockUnits: number | null;
    priceMin: number | null;
    priceMax: number | null;
    costPrice: number | null;
    lastSyncAt: string | null;
    syncStale: boolean;
    imageBlobPath: string | null;
    imagePrincipal: string | null;
}

export interface ProductOriginOption {
    key: string;
    label: string;
}

export interface StoreProductListResult {
    rows: StoreProductRow[];
    total: number;
    counts: Record<string, number>;
    origins: ProductOriginOption[];
    brands: string[];
    collections: string[];
}

export interface StoreProductListParams {
    view: string;
    q: string;
    page: number;
    sort: string | null;
    dir: string | null;
    origin?: string;
    brand?: string;
    collection?: string;
    stock?: string;
    limit?: number;
    offset?: number;
}

export type ProductBatchAction = 'publish' | 'review' | 'draft' | 'activate' | 'deactivate';

const QK = ['admin', 'store-products'] as const;

function num(value: unknown): number | null {
    if (value == null || value === '') return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
}

function asRow(raw: Partial<StoreProductRow>): StoreProductRow {
    return {
        id: String(raw.id ?? ''),
        kind: raw.kind === 'merch' ? 'merch' : 'catalog',
        sourceId: String(raw.sourceId ?? ''),
        name: String(raw.name ?? ''),
        slug: String(raw.slug ?? ''),
        brand: String(raw.brand ?? ''),
        originKey: String(raw.originKey ?? ''),
        originLabel: String(raw.originLabel ?? ''),
        qualityStatus: raw.qualityStatus ?? null,
        isActive: raw.isActive !== false,
        completeness: num(raw.completeness),
        stockCode: String(raw.stockCode ?? ''),
        stockUnits: num(raw.stockUnits),
        priceMin: num(raw.priceMin),
        priceMax: num(raw.priceMax),
        costPrice: num(raw.costPrice),
        lastSyncAt: raw.lastSyncAt ?? null,
        syncStale: raw.syncStale === true,
        imageBlobPath: raw.imageBlobPath ?? null,
        imagePrincipal: raw.imagePrincipal ?? null,
    };
}

export async function fetchStoreProducts(
    userId: string,
    params: StoreProductListParams,
): Promise<StoreProductListResult> {
    const limit = params.limit ?? PRODUCT_PAGE_SIZE;
    const offset = params.offset ?? Math.max(0, params.page - 1) * PRODUCT_PAGE_SIZE;
    const { data, error } = await dbClient.rpc('admin_list_store_products_unified', {
        p_admin_user_id: userId,
        p_view: params.view,
        p_search: params.q.trim() || null,
        p_origin: params.origin || null,
        p_brand: params.brand || null,
        p_collection: params.collection || null,
        p_stock: params.stock || null,
        p_sort: params.sort || 'name',
        p_dir: params.dir || 'asc',
        p_limit: limit,
        p_offset: offset,
    });
    if (error) throw new Error(error.message);
    const payload = (normalizeRpcResponse(data)[0] ?? {}) as {
        items?: Partial<StoreProductRow>[];
        total?: unknown;
        counts?: Record<string, unknown>;
        facets?: {
            origins?: { key?: string; label?: string }[];
            brands?: unknown[];
            collections?: unknown[];
        };
    };
    const counts: Record<string, number> = {};
    for (const [key, value] of Object.entries(payload.counts ?? {})) {
        counts[key] = Number(value) || 0;
    }
    return {
        rows: (payload.items ?? []).map(asRow),
        total: Number(payload.total) || 0,
        counts,
        origins: (payload.facets?.origins ?? [])
            .filter((origin) => origin.key)
            .map((origin) => ({ key: String(origin.key), label: String(origin.label || origin.key) })),
        brands: (payload.facets?.brands ?? []).map((brand) => String(brand)),
        collections: (payload.facets?.collections ?? []).map((slug) => String(slug)),
    };
}

export function useStoreProductsList(params: StoreProductListParams) {
    const { user } = useAuth();
    return useQuery({
        queryKey: [...QK, params],
        enabled: Boolean(user?.id),
        queryFn: () => fetchStoreProducts(user!.id, params),
        staleTime: 15_000,
    });
}

export interface CatalogCollectionTitle {
    slug: string;
    title: string;
}

export function useCatalogCollectionTitles() {
    const { user } = useAuth();
    return useQuery<CatalogCollectionTitle[]>({
        queryKey: ['admin', 'store-products', 'collection-titles'],
        enabled: Boolean(user?.id),
        staleTime: 60_000,
        queryFn: async () => {
            const { data, error } = await dbClient.rpc('admin_list_catalog_collection_titles', {
                p_admin_user_id: user!.id,
            });
            if (error) throw new Error(error.message);
            const payload = (normalizeRpcResponse(data)[0] ?? {}) as { items?: unknown };
            const items = Array.isArray(payload.items) ? payload.items : [];
            return items.flatMap((row) => {
                if (!row || typeof row !== 'object') return [];
                const item = row as { slug?: unknown; title?: unknown };
                const slug = String(item.slug ?? '').trim();
                if (!slug) return [];
                const title = String(item.title ?? '').trim() || slug;
                return [{ slug, title }];
            });
        },
    });
}

export function useSetStoreProductsBatch() {
    const { user } = useAuth();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (input: { action: ProductBatchAction; catalogIds: string[]; merchIds: string[] }) => {
            const { data, error } = await dbClient.rpc('admin_set_store_products_batch', {
                p_admin_user_id: user!.id,
                p_action: input.action,
                p_catalog_ids: input.catalogIds.join(','),
                p_merch_ids: input.merchIds.join(','),
            });
            if (error) throw new Error(error.message);
            return normalizeRpcResponse(data)[0] as { catalogUpdated?: number; merchUpdated?: number };
        },
        onSuccess: () => refreshStoreAfter(queryClient, 'product-batch'),
    });
}
